import { UserDataOutput } from '../@types';
import { injectedIntercom } from '../globals/ConfigureGlobals';
import { injectedStore } from '../globals/StoreAccess';
import { getBrandName } from '../helpers/brandHelpers';

export const IntercomManager = {
  updateBrand(brandCode?: string | null): boolean {
    try {
      const fallbackBrand = (() => {
        try {
          const xBrandId = injectedStore?.getState?.()?.config?.xBrandId;
          const name = getBrandName(xBrandId);
          return (name && name.length ? name : 'getaway').toUpperCase();
        } catch {
          return 'GETAWAY';
        }
      })();
      const rawBrand = (brandCode || fallbackBrand).toString().toUpperCase();
      // Intercom expects "MIS" for the MietSmart brand; getBrandName returns "msi"
      const brandUpper = rawBrand === 'MSI' ? 'MIS' : rawBrand;
      if (injectedIntercom && brandUpper) {
        try {
          injectedIntercom.updateUser({
            customAttributes: { custom_brand: brandUpper },
          });
          return true;
        } catch {
          return false;
        }
      }
      return false;
    } catch {
      return false;
    }
  },
  registerIntercomUser(
    user: UserDataOutput | null | undefined,
    brandCode?: string | null
  ): Promise<boolean> {
    return new Promise((resolve) => {
      if (injectedIntercom) {
        const fallbackBrand = (() => {
          try {
            const xBrandId = injectedStore?.getState?.()?.config?.xBrandId;
            const name = getBrandName(xBrandId);
            // getBrandName returns '' for unknown IDs; fallback to 'getaway'
            return (name && name.length ? name : 'getaway').toUpperCase();
          } catch {
            return 'GETAWAY';
          }
        })();
        const rawBrand = (brandCode || fallbackBrand)
          ?.toString()
          ?.toUpperCase();
        // Intercom expects "MIS" for the MietSmart brand; getBrandName returns "msi"
        const brandUpper = rawBrand === 'MSI' ? 'MIS' : rawBrand;
        if (user) {
          let result: Promise<boolean> = null;
          try {
            const customBrand = brandUpper;
            const name = [user.firstName, user.lastName]
              .filter(Boolean)
              .join(' ');

            const customAttributes: Record<string, any> = {
              custom_brand: customBrand,
              custom_user_status: user?.blocked ? 'blocked' : 'active',
              custom_block_reason:
                (user as any)?.blockReasonDetail || undefined,
              custom_tariff: user?.tariff?.name || undefined,
              custom_operatorID: (user as any)?.merchantSystemData?.[0]
                ?.merchantReference
                ? `${
                    (user as any)?.merchantSystemData?.[0]?.merchantReference
                  }_${(user as any)?.merchantSystemData?.[0]?.merchantSystem}`
                : '',
            };

            // Remove undefined values to avoid sending invalid keys
            Object.keys(customAttributes).forEach(
              (k) =>
                customAttributes[k] === undefined && delete customAttributes[k]
            );

            const attrs: any = {
              userId: user.id,
              email: user.email,
              ...(name ? { name } : {}),
              customAttributes,
            };

            if (
              Object.prototype.hasOwnProperty.call(
                injectedIntercom,
                'loginUserWithUserAttributes'
              )
            ) {
              result = injectedIntercom.loginUserWithUserAttributes(attrs);
            } else {
              result = injectedIntercom.loginUserWithUserAttributes(attrs);
            }
            result
              .then(() => {
                // Handle the resolved Promise
                resolve(true);
              })
              .catch(() => {
                // Handle the rejected Promise
                // Perform error handling
                resolve(false);
              });
          } catch (e) {
            // @ts-ignore
            // eslint-disable-next-line no-undef
            if (__DEV__) {
              console.log('Intercom register user:', e);
            }
            resolve(false);
          }
        } else {
          try {
            if (
              Object.prototype.hasOwnProperty.call(
                injectedIntercom,
                'loginUnidentifiedUser'
              )
            ) {
              injectedIntercom.loginUnidentifiedUser();
              // Set brand for guest users as well
              try {
                injectedIntercom.updateUser({
                  customAttributes: { custom_brand: brandUpper },
                });
              } catch {
                // no-op
              }
            } else {
              injectedIntercom.loginUnidentifiedUser();
              // Set brand for guest users as well
              try {
                injectedIntercom.updateUser({
                  customAttributes: { custom_brand: brandUpper },
                });
              } catch {
                // no-op
              }
            }
            resolve(true);
          } catch {
            resolve(false);
          }
        }
      } else {
        resolve(false); // Intercom is not available
      }
    });
  },

  logout() {
    if (injectedIntercom) {
      injectedIntercom.logout();
    }
  },
};
